import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import ServiceBadge from "../components/ServiceBadge";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  ClipboardList,
  Download,
  Loader2,
  Calendar,
  FileText,
  CheckCircle,
  AlertCircle,
  Truck,
  User,
  Clock,
  Trash2,
  Eye,
  PenLine
} from "lucide-react";
import { toast } from "sonner";
import Pagination from "../components/Pagination";

const ManifestPage = () => {
  const [waybills, setWaybills] = useState([]);
  const [manifests, setManifests] = useState([]);
  const [selectedWaybills, setSelectedWaybills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("create"); // "create" or "history"
  const [showSignOffModal, setShowSignOffModal] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [signOffData, setSignOffData] = useState({ driver_name: "", vehicle_reg: "", collection_time: "", notes: "" });
  const [driverName, setDriverName] = useState("");
  const [vehicleReg, setVehicleReg] = useState("");
  const [manifestNotes, setManifestNotes] = useState("");
  const [waybillPage, setWaybillPage] = useState(1);
  const [waybillPageSize, setWaybillPageSize] = useState(10);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);

  useEffect(() => {
    fetchWaybills();
    fetchManifests();
  }, []);

  const fetchWaybills = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/waybills`);
      setWaybills(response.data.filter(w => !w.manifested));
    } catch (error) {
      console.error("Error fetching waybills:", error);
      toast.error("Failed to load waybills");
    } finally {
      setLoading(false);
    }
  };

  const fetchManifests = async () => {
    try {
      const response = await axios.get(`${API}/manifests`);
      setManifests(response.data);
    } catch (error) {
      console.error("Error fetching manifests:", error);
    }
  };

  const toggleSelect = (waybillId) => {
    setSelectedWaybills(prev => 
      prev.includes(waybillId) 
        ? prev.filter(id => id !== waybillId)
        : [...prev, waybillId]
    );
  };

  const selectAll = () => {
    if (selectedWaybills.length === waybills.length) {
      setSelectedWaybills([]);
    } else {
      setSelectedWaybills(waybills.map(w => w.id));
    }
  };

  const generateManifest = async () => {
    if (selectedWaybills.length === 0) {
      toast.error("Please select at least one waybill");
      return;
    }

    try {
      const response = await axios.post(`${API}/manifests`, {
        waybill_ids: selectedWaybills,
        driver_name: driverName,
        vehicle_reg: vehicleReg,
        notes: manifestNotes,
      });
      toast.success(`Manifest ${response.data.manifest_number} created`);
      setSelectedWaybills([]);
      setDriverName("");
      setVehicleReg("");
      setManifestNotes("");
      fetchWaybills();
      fetchManifests();
      setActiveTab("history");
    } catch (error) {
      toast.error("Failed to create manifest");
    }
  };

  const downloadManifestPDF = async (manifestId, manifestNumber) => {
    try {
      const response = await axios.get(`${API}/manifests/${manifestId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manifest-${manifestNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Manifest PDF downloaded");
    } catch (error) {
      toast.error("Failed to download manifest PDF");
    }
  };

  const handleSignOff = async () => {
    if (!selectedManifest) return;
    try {
      await axios.put(`${API}/manifests/${selectedManifest.id}/sign-off`, signOffData);
      toast.success("Manifest signed off");
      setShowSignOffModal(false);
      setSelectedManifest(null);
      setSignOffData({ driver_name: "", vehicle_reg: "", collection_time: "", notes: "" });
      fetchManifests();
    } catch (error) {
      toast.error("Failed to sign off manifest");
    }
  };

  const deleteManifest = async (manifestId, manifestNumber) => {
    if (!window.confirm(`Delete manifest ${manifestNumber}? Waybills will be un-manifested.`)) return;
    try {
      await axios.delete(`${API}/manifests/${manifestId}`);
      toast.success(`Manifest ${manifestNumber} deleted`);
      fetchManifests();
      fetchWaybills();
    } catch (error) {
      toast.error("Failed to delete manifest");
    }
  };

  const openSignOff = (manifest) => {
    setSelectedManifest(manifest);
    setSignOffData({
      driver_name: manifest.driver_name || "",
      vehicle_reg: manifest.vehicle_reg || "",
      collection_time: new Date().toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }),
      notes: manifest.notes || ""
    });
    setShowSignOffModal(true);
  };

  const today = new Date().toISOString().split('T')[0];

  // Pagination for waybills (Create tab)
  const paginatedWaybills = useMemo(() => {
    const start = (waybillPage - 1) * waybillPageSize;
    return waybills.slice(start, start + waybillPageSize);
  }, [waybills, waybillPage, waybillPageSize]);
  const waybillTotalPages = Math.ceil(waybills.length / waybillPageSize);

  // Pagination for manifests (History tab)
  const paginatedManifests = useMemo(() => {
    const start = (historyPage - 1) * historyPageSize;
    return manifests.slice(start, start + historyPageSize);
  }, [manifests, historyPage, historyPageSize]);
  const historyTotalPages = Math.ceil(manifests.length / historyPageSize);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Manifest
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Create collection manifests for courier pickup
          </p>
        </div>
        <Badge variant="outline" className="text-slate-600" data-testid="manifest-date-badge">
          <Calendar className="w-3 h-3 mr-1" />
          {today}
        </Badge>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 rounded-lg p-1 w-fit" data-testid="manifest-tabs">
        <button
          onClick={() => setActiveTab("create")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "create" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-create"
        >
          Create Manifest
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === "history" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="tab-history"
        >
          History ({manifests.length})
        </button>
      </div>

      {activeTab === "create" ? (
        <>
          {/* Driver Details */}
          <Card className="border-slate-200 shadow-sm mb-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Truck className="w-4 h-4 text-slate-500" />
                Collection Details (Optional)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Driver Name</Label>
                  <Input
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    placeholder="Enter driver name"
                    className="border-slate-200 mt-1"
                    data-testid="driver-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Vehicle Registration</Label>
                  <Input
                    value={vehicleReg}
                    onChange={(e) => setVehicleReg(e.target.value)}
                    placeholder="e.g. CA 123-456"
                    className="border-slate-200 mt-1"
                    data-testid="vehicle-reg-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Notes</Label>
                  <Input
                    value={manifestNotes}
                    onChange={(e) => setManifestNotes(e.target.value)}
                    placeholder="Any collection notes..."
                    className="border-slate-200 mt-1"
                    data-testid="manifest-notes-input"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Waybills List */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Ready for Manifest</CardTitle>
                <div className="flex items-center gap-3">
                  {waybills.length > 0 && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={selectAll}
                      className="text-sm"
                      data-testid="select-all-btn"
                    >
                      {selectedWaybills.length === waybills.length ? "Deselect All" : "Select All"}
                    </Button>
                  )}
                  <Button 
                    onClick={generateManifest}
                    disabled={selectedWaybills.length === 0}
                    className="bg-[#16a34a] hover:bg-[#15803d] text-white"
                    data-testid="generate-manifest-btn"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Generate Manifest ({selectedWaybills.length})
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-48">
                  <Loader2 className="w-6 h-6 animate-spin text-[#16a34a]" />
                </div>
              ) : waybills.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-300 mb-3" />
                  <p className="text-slate-600 font-medium">All caught up!</p>
                  <p className="text-sm text-slate-500 mt-1">No waybills pending for manifest</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {paginatedWaybills.map((waybill) => (
                    <div 
                      key={waybill.id}
                      className={`flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors cursor-pointer ${
                        selectedWaybills.includes(waybill.id) ? "bg-orange-50" : ""
                      }`}
                      onClick={() => toggleSelect(waybill.id)}
                      data-testid={`manifest-waybill-${waybill.waybill_number}`}
                    >
                      <Checkbox 
                        checked={selectedWaybills.includes(waybill.id)}
                        onCheckedChange={() => toggleSelect(waybill.id)}
                        className="data-[state=checked]:bg-[#16a34a] data-[state=checked]:border-[#16a34a]"
                      />
                      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-mono font-medium text-[#16a34a]">{waybill.waybill_number}</p>
                        <p className="text-sm text-slate-500">Ref: {waybill.reference || `Order #${waybill.order_number}`}</p>
                        {waybill.service_type && (
                          <ServiceBadge
                            service={waybill.service_type}
                            size="xs"
                            className="mt-1"
                            testId={`manifest-service-${waybill.waybill_number}`}
                          />
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-900">{waybill.recipient?.name || waybill.destination}</p>
                        <p className="text-xs text-slate-500">{waybill.recipient?.city}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="text-slate-500">
                          {waybill.num_parcels || 1} parcel(s)
                        </Badge>
                        {waybill.total_weight > 0 && (
                          <p className="text-xs text-slate-400 mt-1">{waybill.total_weight} kg</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {waybills.length > 0 && (
                <Pagination
                  currentPage={waybillPage}
                  totalPages={waybillTotalPages}
                  totalItems={waybills.length}
                  pageSize={waybillPageSize}
                  onPageChange={setWaybillPage}
                  onPageSizeChange={(size) => { setWaybillPageSize(size); setWaybillPage(1); }}
                />
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        /* Manifest History */
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="text-base">Manifest History</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {manifests.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <ClipboardList className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-600 font-medium">No manifests yet</p>
                <p className="text-sm text-slate-500 mt-1">Create your first manifest from the Create tab</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {paginatedManifests.map((manifest) => (
                  <div key={manifest.id} className="p-4 hover:bg-slate-50 transition-colors" data-testid={`manifest-row-${manifest.manifest_number}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          manifest.signed_off ? 'bg-green-100' : 'bg-orange-100'
                        }`}>
                          {manifest.signed_off ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <ClipboardList className="w-5 h-5 text-[#16a34a]" />
                          )}
                        </div>
                        <div>
                          <p className="font-mono font-medium text-slate-900">{manifest.manifest_number}</p>
                          <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {manifest.collection_date || manifest.created_at?.substring(0, 10)}
                            </span>
                            <span>{manifest.waybill_count} waybill(s)</span>
                            <span>{manifest.total_parcels} parcel(s)</span>
                            {manifest.total_weight > 0 && (
                              <span>{manifest.total_weight.toFixed(1)} kg</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {manifest.driver_name && (
                          <div className="text-right mr-2">
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <User className="w-3 h-3" /> {manifest.driver_name}
                            </p>
                            {manifest.vehicle_reg && (
                              <p className="text-xs text-slate-400 flex items-center gap-1">
                                <Truck className="w-3 h-3" /> {manifest.vehicle_reg}
                              </p>
                            )}
                          </div>
                        )}
                        <Badge className={manifest.signed_off 
                          ? "bg-green-100 text-green-800 border-green-200" 
                          : "bg-amber-100 text-amber-800 border-amber-200"
                        }>
                          {manifest.signed_off ? "Collected" : "Pending"}
                        </Badge>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadManifestPDF(manifest.id, manifest.manifest_number)}
                            className="text-slate-600 hover:text-[#16a34a] p-2"
                            title="Download PDF"
                            data-testid={`download-manifest-${manifest.manifest_number}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {!manifest.signed_off && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openSignOff(manifest)}
                              className="text-slate-600 hover:text-green-600 p-2"
                              title="Driver Sign-Off"
                              data-testid={`signoff-manifest-${manifest.manifest_number}`}
                            >
                              <PenLine className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteManifest(manifest.id, manifest.manifest_number)}
                            className="text-slate-600 hover:text-red-600 p-2"
                            title="Delete Manifest"
                            data-testid={`delete-manifest-${manifest.manifest_number}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {manifests.length > 0 && (
              <Pagination
                currentPage={historyPage}
                totalPages={historyTotalPages}
                totalItems={manifests.length}
                pageSize={historyPageSize}
                onPageChange={setHistoryPage}
                onPageSizeChange={(size) => { setHistoryPageSize(size); setHistoryPage(1); }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Sign-Off Modal */}
      <Dialog open={showSignOffModal} onOpenChange={setShowSignOffModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-[#16a34a]" />
              Driver Sign-Off
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs text-slate-500">Manifest</p>
              <p className="font-mono font-medium">{selectedManifest?.manifest_number}</p>
              <p className="text-xs text-slate-500 mt-1">
                {selectedManifest?.waybill_count} waybill(s) | {selectedManifest?.total_parcels} parcel(s)
              </p>
            </div>
            <div>
              <Label className="text-sm">Driver Name</Label>
              <Input
                value={signOffData.driver_name}
                onChange={(e) => setSignOffData(prev => ({...prev, driver_name: e.target.value}))}
                placeholder="Driver's full name"
                className="mt-1"
                data-testid="signoff-driver-name"
              />
            </div>
            <div>
              <Label className="text-sm">Vehicle Registration</Label>
              <Input
                value={signOffData.vehicle_reg}
                onChange={(e) => setSignOffData(prev => ({...prev, vehicle_reg: e.target.value}))}
                placeholder="e.g. CA 123-456"
                className="mt-1"
                data-testid="signoff-vehicle-reg"
              />
            </div>
            <div>
              <Label className="text-sm">Collection Time</Label>
              <Input
                value={signOffData.collection_time}
                onChange={(e) => setSignOffData(prev => ({...prev, collection_time: e.target.value}))}
                placeholder="e.g. 14:30"
                className="mt-1"
                data-testid="signoff-collection-time"
              />
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Input
                value={signOffData.notes}
                onChange={(e) => setSignOffData(prev => ({...prev, notes: e.target.value}))}
                placeholder="Any notes..."
                className="mt-1"
                data-testid="signoff-notes"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                onClick={() => setShowSignOffModal(false)} 
                variant="outline" 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSignOff}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-signoff-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Confirm Collection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManifestPage;
